import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

// Components
import { Button, Col, Modal, Row, Text } from 'components';
import { theme } from 'lib';

/*
  Confirmation step before casting a Half-Time prediction — web port of the
  mobile ConfirmationDialog (src/screens/Profile/components/PredictionsSection/
  ConfirmationDialog.tsx). onConfirm resolves to a boolean: the modal only
  closes (via onSuccess) when the vote actually went through, otherwise it
  stays open with an inline error so the fan can retry.
*/

const TeamEmblem = styled.img`
  width: 42px;
  height: 42px;
  border-radius: 16px;
  object-fit: cover;
`;

const PredictionConfirmModal = ({
  visible,
  selectedSegment,
  game,
  onConfirm,
  onCancel,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { isMobile } = useSelector((state) => state.appState);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleConfirm() {
    setLoading(true);
    setError(null);

    try {
      const success = await onConfirm();

      if (success) {
        onSuccess?.();
        return;
      }

      setError(t('callToAction.predictionVoteFailed'));
    } catch (err) {
      setError(t('callToAction.predictionVoteFailed'));
    } finally {
      setLoading(false);
    }
  }

  function handleCancel() {
    if (loading) return;
    setError(null);
    onCancel();
  }

  return (
    <Modal
      visible={visible}
      closeModal={handleCancel}
      noOutsideClick={loading}
      wid={isMobile ? '320px !important' : '400px !important'}
    >
      <Col pad="32px 24px" center wid="100%" boxSizing="border-box">
        <Text h3 center>
          {t('callToAction.confirmHalfTimePrediction')}
        </Text>

        {selectedSegment && (
          <Col noFlex centerAll wid="100%" marg="24px 0 0">
            <Text
              size="15px"
              bold
              center
              style={{ textTransform: 'uppercase', letterSpacing: 1.1 }}
            >
              {t('callToAction.yourPick')}
            </Text>
            <Text size="26px" bold center marg="6px 0 0">
              {selectedSegment.label}
            </Text>

            {/* equal-flex sides keep "vs" centered even if one emblem is missing */}
            <Row centerAll noFlex marg="6px 0 0">
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                {game?.home?.avatar && <TeamEmblem src={game.home.avatar} alt="" />}
              </div>
              <Text size="15px" bold style={{ margin: '0 6px' }}>
                vs
              </Text>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                {game?.away?.avatar && <TeamEmblem src={game.away.avatar} alt="" />}
              </div>
            </Row>
          </Col>
        )}

        {error && (
          <Text b1 center col={theme.SECONDARY} marg="20px 0 0">
            {error}
          </Text>
        )}

        <Col noFlex centerAll wid="100%" marg="24px 0 0">
          <Button
            big
            wid="100%"
            bg={theme.SECONDARY}
            text={t('callToAction.yesSubmit')}
            loading={loading}
            disabled={loading}
            onClick={handleConfirm}
          />
          <Button
            big
            wid="100%"
            marg="16px 0 0"
            bordered
            text={t('common.cancel')}
            disabled={loading}
            onClick={handleCancel}
          />
        </Col>
      </Col>
    </Modal>
  );
};

export default PredictionConfirmModal;
